import React, { useEffect, useState } from 'react';
import { HandwrittenSignatureField } from './HandwrittenSignatureField';
import { useUserProfile } from '../context/UserProfileContext';
import { splitProfileSignature } from '../utils/userProfile';

const inputClass =
  'px-3 py-2 rounded bg-black border border-gray-700 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:border-transparent w-full';
const labelClass = 'block text-xs text-gray-400 mb-1';

const WORKER_ROWS = Array.from({ length: 8 }, (_, i) => i);

/**
 * Lightweight acknowledgement / signoff / plan stub used for forms that are not fully
 * templated yet (H&S acknowledgement, SWP signoff, confined space entry plan).
 */
export function SimpleSignoffForm({
  heading,
  intro,
  showSiteFields = true,
  showWorkerSignoff = true,
}) {
  const { name: profileName, signature: profileSignature } = useUserProfile();
  const [completedBy, setCompletedBy] = useState(profileName || '');
  const [date, setDate] = useState('');
  const [siteName, setSiteName] = useState('');
  const [notes, setNotes] = useState('');
  const [signature, setSignature] = useState(() => {
    const split = splitProfileSignature(profileSignature);
    return { mode: split.image ? 'draw' : 'type', ...split };
  });
  const [workers, setWorkers] = useState(() =>
    WORKER_ROWS.map(() => ({ name: '', date: '', signature: '', signatureImage: '' }))
  );

  useEffect(() => {
    if (profileName) setCompletedBy((prev) => prev || profileName);
    if (profileSignature) {
      const split = splitProfileSignature(profileSignature);
      setSignature((prev) =>
        prev.text || prev.image
          ? prev
          : { mode: split.image ? 'draw' : 'type', ...split }
      );
    }
  }, [profileName, profileSignature]);

  const updateWorker = (idx, patch) => {
    setWorkers((prev) => prev.map((row, i) => (i === idx ? { ...row, ...patch } : row)));
  };

  return (
    <form className="space-y-6 text-gray-200" onSubmit={(e) => e.preventDefault()}>
      <div>
        <h3 className="text-base font-semibold text-white mb-1">{heading}</h3>
        {intro ? <p className="text-sm text-gray-400">{intro}</p> : null}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Completed by</label>
          <input
            type="text"
            value={completedBy}
            onChange={(e) => setCompletedBy(e.target.value)}
            className={inputClass}
          />
        </div>
        {showSiteFields ? (
          <div className="sm:col-span-2">
            <label className={labelClass}>Site / project</label>
            <input
              type="text"
              value={siteName}
              onChange={(e) => setSiteName(e.target.value)}
              className={inputClass}
              placeholder="Site or project name"
            />
          </div>
        ) : null}
      </div>

      <div>
        <label className={labelClass}>Notes / plan details</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={4}
          className={inputClass}
          placeholder="Add acknowledgement details, SWP references, or plan notes…"
        />
      </div>

      {showWorkerSignoff ? (
        <div>
          <h4 className="text-sm font-medium text-amber-200/90 mb-2">Worker signoff</h4>
          <div className="mb-2 flex items-center gap-2">
            <p className="min-w-0 flex-1 text-xs font-medium text-gray-300">Worker name / Signature</p>
            <p className="w-[10.5rem] shrink-0 text-xs font-medium text-gray-300">Date</p>
          </div>
          {workers.map((row, idx) => (
            <div key={`worker-${idx}`} className="border-b border-gray-700 py-3 last:border-b-0">
              <div className="mb-2">
                <input
                  type="text"
                  value={row.name}
                  onChange={(e) => updateWorker(idx, { name: e.target.value })}
                  placeholder="Worker name"
                  className={inputClass}
                />
              </div>
              <HandwrittenSignatureField
                textValue={row.signature}
                imageValue={row.signatureImage || ''}
                onTextChange={(value) => updateWorker(idx, { signature: value })}
                onImageChange={(value) => updateWorker(idx, { signatureImage: value })}
                dateValue={row.date}
                onDateChange={(value) => updateWorker(idx, { date: value })}
                placeholder="Type signature"
                inputClassName={inputClass}
              />
            </div>
          ))}
        </div>
      ) : null}

      <div className="space-y-2 border-t border-gray-800 pt-4">
        <div className="flex items-center gap-2">
          <p className="min-w-0 flex-1 text-xs font-medium text-gray-300">Completed by signature</p>
          <p className="w-[10.5rem] shrink-0 text-xs font-medium text-gray-300">Date</p>
        </div>
        <HandwrittenSignatureField
          textValue={signature.text}
          imageValue={signature.image}
          onTextChange={(text) => setSignature((prev) => ({ ...prev, text }))}
          onImageChange={(image) => setSignature((prev) => ({ ...prev, image }))}
          dateValue={date}
          onDateChange={setDate}
          inputClassName={inputClass}
        />
      </div>
    </form>
  );
}

export function HsAcknowledgementForm() {
  return (
    <SimpleSignoffForm
      heading="H&S Acknowledgement"
      intro="Complete once per year."
    />
  );
}

export function SwpSignoffForm() {
  return (
    <SimpleSignoffForm
      heading="SWP Signoff Sheet"
      intro="Safe work procedure signoff — once per year."
    />
  );
}

export function ConfinedSpaceEntryPlanForm() {
  return (
    <SimpleSignoffForm
      heading="Confined Space Entry Plan"
      intro="Complete when a confined space entry plan is required for the worksite."
      showWorkerSignoff
    />
  );
}
